// Multi-manager buyer teams — this team's leads (Q8 A).
//
// 🔴 A READ OF THE ORDINARY BUYERS LIST, NARROWED TO ONE TEAM — not a second
// queue. `buyersApi.list` with `buyerTeamId` is the same server query the
// buyers page runs, paged the same way, under the same key factory, so this
// table cannot disagree with that page about who is on the team. Assigning
// still happens on the assignment queue, where the filters, the sort and the
// bulk actions live; the specialist column here is prose, never a control.
//
// 🔴 THE DEFINITION IS STATED WHERE THE ROWS ARE READ. "This team's leads" means
// leads whose `buyerTeamId` is this team. The specialist WORKING a lead may sit
// on another team (D2) — a lead keeps its owner when it changes team — so the
// specialist column is not a roster and is not expected to match one.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ListChecks } from "lucide-react";
import { buyersApi, type BuyerLeadListParams, type BuyerLeadSummaryDto } from "@/api/buyers";
import type { BuyerTeamDetailDto } from "@/api/buyerTeams";
import {
  Card,
  Skeleton,
  StatCard,
  StatsBar,
  StatusBadge,
  SubTable,
  SUB_TABLE_CHROME_THRESHOLD,
  Timestamp,
  buttonVariants,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { useListFilters, type ListFilterDefs } from "@/hooks/useListFilters";
import { buyerLabel } from "../buyerFilters";
import { buyerStatusTone } from "../buyerListBits";
import { BuyerPager } from "../BuyerPager";
import { QueryErrorState } from "../queryStates";

/** The server's page size, and therefore the threshold that keeps `SubTable`
 *  from paging an already-paged page. `BuyerPager` owns the real one. */
const PAGE_SIZE = 25;

/**
 * No filters — only the page. The descriptor is empty rather than absent so
 * the page number round-trips through the query string the way every other
 * paged list's does, and survives a tab change that keeps it.
 */
const TEAM_QUEUE_FILTERS = {} as const satisfies ListFilterDefs;

const columns: SubTableColumn<BuyerLeadSummaryDto>[] = [
  {
    key: "buyer",
    header: "Buyer",
    searchText: (b) => `${b.staffDisplayName ?? b.displayName} ${b.email ?? ""}`,
    render: (b) => (
      <div className="min-w-0">
        <Link to={`/buyers/${b.id}`} className="text-gold-shadow hover:underline">
          {b.staffDisplayName ?? b.displayName}
        </Link>
        {b.email && <div className="text-caption text-gray-mid">{b.email}</div>}
      </div>
    ),
  },
  {
    key: "status",
    header: "Status",
    searchText: (b) => buyerLabel(b.status),
    render: (b) => (
      <StatusBadge tone={buyerStatusTone(b.status)}>
        {buyerLabel(b.status)}
      </StatusBadge>
    ),
  },
  {
    key: "specialist",
    header: "Consultant",
    // Prose, never a control: reassigning is the assignment queue's job. §6 —
    // state ABSENT is muted prose, never a pill; an unowned lead is an ordinary
    // state of a young team, and pilling it makes every row look like a problem.
    render: (b) =>
      b.assignedSpecialistName ?? <span className="text-gray-mid italic">Unassigned</span>,
  },
  { key: "source", header: "Source", render: (b) => buyerLabel(b.source) },
  {
    key: "created",
    header: "Created",
    render: (b) => <Timestamp value={b.createdAt} absolute className="text-gray-mid" />,
  },
];

export function QueueTab({ team }: { team: BuyerTeamDetailDto }) {
  const queueHref = `/buyer-assignments?buyerTeamId=${encodeURIComponent(team.id)}`;
  const filters = useListFilters(TEAM_QUEUE_FILTERS);
  const { page } = filters;

  const params = useMemo<BuyerLeadListParams>(
    () => ({ page, pageSize: PAGE_SIZE, buyerTeamId: team.id }),
    [page, team.id],
  );

  const list = useQuery({
    queryKey: buyerKeys.list(params),
    queryFn: () => buyersApi.list(params),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.totalCount ?? 0;

  return (
    <div className="flex flex-col gap-space-4" data-testid="buyer-team-queue-tab">
      <div className="flex flex-wrap items-end justify-between gap-space-3">
        <StatsBar className="mb-0">
          <StatCard label="Waiting for an owner" value={team.unassignedLeadCount} />
        </StatsBar>
        <Link
          to={queueHref}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
          data-testid="buyer-team-queue-link"
        >
          <ListChecks size={14} strokeWidth={1.5} aria-hidden /> Open this team&apos;s queue
        </Link>
      </div>

      <Card>
        <h2 className="font-display text-h3 text-onyx m-0">Leads on this team</h2>
        {/* The D2 rule, stated where the rows it governs are read. */}
        <p className="text-caption text-gray-mid m-0 mt-space-1 mb-space-3 max-w-reading">
          Leads whose team is this team. The consultant working a lead may sit on another
          team.
        </p>

        {list.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : list.isError ? (
          /* Before the empty branch: "no leads on this team" is a claim about
             the team, and a failed read is no evidence for it. */
          <QueryErrorState
            what="this team's leads"
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        ) : (
          <>
            <SubTable<BuyerLeadSummaryDto>
              caption={`Leads on ${team.name}`}
              data-testid="buyer-team-queue-table"
              rows={rows}
              rowKey={(b) => b.id}
              columns={columns}
              // Paged SERVER-side (BuyerPager, below), so SubTable must never
              // page the page: its threshold is the page size, which the rows
              // in hand can never exceed.
              chromeThreshold={PAGE_SIZE}
              empty={{
                message: "No leads are on this team yet.",
                // A door is DECLARED or omitted. Nothing on this tab creates a
                // lead: leads land here by routing at intake, or when a manager
                // sends them here from the assignment queue.
                door: {
                  kind: "derived",
                  explain:
                    "Leads land here by routing at intake, or when a manager sends them to this team from the assignment queue.",
                },
              }}
            />
            <BuyerPager
              filters={filters}
              total={total}
              shown={rows.length}
              pageSize={PAGE_SIZE}
              hidden={total < SUB_TABLE_CHROME_THRESHOLD}
            />
          </>
        )}
      </Card>
    </div>
  );
}
